import React, { useState } from 'react';
import { X, AlertTriangle, FileText } from 'lucide-react';

const DeleteConfirmationModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  userName, 
  loading = false 
}) => {
  const [confirmText, setConfirmText] = useState('');
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (confirmText !== 'SLETT') {
      setError('Du må skrive "SLETT" for å bekrefte slettingen');
      return;
    }
    onConfirm();
  };

  const handleClose = () => {
    setConfirmText('');
    setError('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-gray-800 rounded-xl shadow-2xl border border-red-500/30 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-red-600 to-red-700 px-6 py-4 border-b border-red-500/30 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="h-6 w-6 text-white" />
            <h2 className="text-xl font-bold text-white">⚠️ ADVARSEL: PERMANENT SLETTING</h2>
          </div>
          <button
            onClick={handleClose}
            disabled={loading}
            className="text-white/80 hover:text-white transition-colors disabled:opacity-50"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* User Info */}
          <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-4">
            <p className="text-gray-300 text-lg">
              <span className="text-gray-400">Bruker:</span>{' '}
              <span className="font-semibold text-white">"{userName}"</span>
            </p>
          </div>

          {/* Export Warning - Prominent */}
          <div className="bg-yellow-600/20 border-2 border-yellow-500 rounded-lg p-5">
            <div className="flex items-start space-x-3">
              <FileText className="h-6 w-6 text-yellow-500 flex-shrink-0 mt-1" />
              <div className="space-y-2">
                <h3 className="text-yellow-500 font-bold text-lg">
                  ⚠️ DU MÅ FØRST EKSPORTERE TRENINGSLOGGEN!
                </h3>
                <p className="text-yellow-100/90 text-base leading-relaxed">
                  Gå til brukerens profil og eksporter treningsloggen til PDF{' '}
                  <span className="font-bold underline">FØR</span> du sletter.
                </p>
              </div>
            </div>
          </div>

          {/* What will be deleted */}
          <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-5">
            <h3 className="text-red-400 font-bold text-lg mb-4">
              Dette sletter PERMANENT:
            </h3>
            <ul className="space-y-3 text-gray-300">
              <li className="flex items-start space-x-3">
                <span className="text-red-500 font-bold mt-1">•</span>
                <span className="text-base">Brukerens konto</span>
              </li>
              <li className="flex items-start space-x-3">
                <span className="text-red-500 font-bold mt-1">•</span>
                <span className="text-base">ALLE treningsøkter</span>
              </li>
              <li className="flex items-start space-x-3">
                <span className="text-red-500 font-bold mt-1">•</span>
                <span className="text-base">Alle dokumenter (startkort, medlemskort, våpensertifikater)</span>
              </li>
              <li className="flex items-start space-x-3">
                <span className="text-red-500 font-bold mt-1">•</span>
                <span className="text-base">All innloggingshistorikk</span>
              </li>
            </ul>
          </div>

          {/* Final Warning */}
          <div className="bg-red-950/50 border border-red-600/50 rounded-lg p-4">
            <p className="text-red-400 text-center font-bold text-base">
              Etter sletting er det INGEN måte å få dette tilbake på!
            </p>
          </div>

          {/* Confirmation Input */}
          <div className="space-y-3">
            <label className="block">
              <span className="text-gray-300 font-medium text-base mb-2 block">
                For å bekrefte, skriv <span className="text-red-400 font-bold">SLETT</span> i boksen nedenfor:
              </span>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => {
                  setConfirmText(e.target.value);
                  setError('');
                }}
                disabled={loading}
                placeholder="Skriv SLETT her"
                className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent disabled:opacity-50 text-base"
              />
            </label>
            {error && (
              <p className="text-red-400 text-sm flex items-center space-x-2">
                <AlertTriangle className="h-4 w-4" />
                <span>{error}</span>
              </p>
            )}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-gray-900/95 border-t border-gray-700 px-6 py-4 flex space-x-3">
          <button
            onClick={handleClose}
            disabled={loading}
            className="flex-1 py-3 px-4 bg-gray-600 text-white rounded-lg font-medium hover:bg-gray-500 transition-colors disabled:opacity-50 text-base"
          >
            Avbryt
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading || confirmText !== 'SLETT'}
            className="flex-1 py-3 px-4 bg-red-600 text-white rounded-lg font-bold hover:bg-red-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-base"
          >
            {loading ? 'Sletter...' : 'Bekreft sletting'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteConfirmationModal;
